import * as angular from 'angular';
import { find, omit } from 'lodash';

import {
  COLUMN_TYPES,
  CURRENCY_TYPES,
  CurrencyType,
  DATE_FORMATS,
  DECIMAL_STYLES,
  FormatOption,
  THOUSAND_DIGITS,
} from '../../common/constants';
import { tryParseInt } from '@mode/shared/util-js';
import template from './format-settings.html';

class FormatSettingsController {
  formatOptions: any;
  onChange: any;
  enabledFormats: any;
  formatTypes = COLUMN_TYPES;
  currencyTypes = CURRENCY_TYPES;
  decimalStyles = DECIMAL_STYLES;
  thousandDigits = THOUSAND_DIGITS;
  datePresets = DATE_FORMATS;

  MAX_DECIMAL_PLACES = 20;

  symbols = [
    { name: '$', value: '$' },
    { name: 'Euro', value: 'euro' },
  ];

  columnTypeMenuOpen = false;
  currencyTypeMenuOpen = false;
  datePresetMenuOpen = false;

  enabledFormatTypes() {
    if (this.enabledFormats && this.enabledFormats.length) {
      return this.formatTypes.filter((v, i) => !!~this.enabledFormats.indexOf(v.id));
    } else {
      return this.formatTypes;
    }
  }

  hasOptions() {
    return this.isInColumnTypes('currency', 'financial', 'number', 'percentage', 'date');
  }

  isCurrencyType() {
    return this.isInColumnTypes('currency', 'financial');
  }

  isCustomCurrencyType() {
    return this.isCurrencyType() && this.formatOptions[this.formatOptions.type].type === 'custom';
  }

  isDateType() {
    return this.isInColumnTypes('date');
  }

  hasDecimalOptions() {
    return this.isCustomCurrencyType() || this.isInColumnTypes('number', 'percentage');
  }

  hasThousandsOptions() {
    return this.isCustomCurrencyType() || this.isInColumnTypes('number');
  }

  getSampleCurrencyFormat(type: CurrencyType) {
    if (type.id !== 'custom') {
      return `${type.symbol}1${type.thousandSeparator}${'00000'.slice(-type.thousandGroup)}${type.decimalStyle}00`;
    }

    return '';
  }

  getName(id: string, values: Array<FormatOption>) {
    const value = find(values, { id });
    return value ? value.name : values[0].name;
  }

  closeColumnTypeMenu() {
    this.columnTypeMenuOpen = false;
  }

  toggleColumnTypeMenu() {
    this.columnTypeMenuOpen = !this.columnTypeMenuOpen;
  }

  closeCurrencyTypeMenu() {
    this.currencyTypeMenuOpen = false;
  }

  toggleCurrencyTypeMenu() {
    this.currencyTypeMenuOpen = !this.currencyTypeMenuOpen;
  }

  closeDatePresetMenu() {
    this.datePresetMenuOpen = false;
  }

  toggleDatePresetMenu() {
    this.datePresetMenuOpen = !this.datePresetMenuOpen;
  }

  selectType(type) {
    this.formatOptions.type = type.id;

    // Set a reasonable default for the column type settings
    if (!this.formatOptions[type.id]) {
      this.formatOptions[type.id] = type.defaultSettings();
    }

    this.pushChange();
    this.closeColumnTypeMenu();
  }

  selectCurrencyType(type: CurrencyType) {
    if (type.id === 'custom') {
      // Default to previous currency type, or USD
      const defaultSettings = find(CURRENCY_TYPES, { id: this.formatOptions[this.formatOptions.type].type || 'usd' });

      // make a copy of currency settings without the name and id fields to set as the custom default
      this.formatOptions[this.formatOptions.type] = omit(defaultSettings, ['name', 'id']);
    }

    this.formatOptions[this.formatOptions.type].type = type.id;
    this.pushChange();
    this.closeCurrencyTypeMenu();
  }

  selectDatePreset(preset) {
    this.formatOptions[this.formatOptions.type].preset = preset.id;
    this.pushChange();
    this.closeDatePresetMenu();
  }

  pushChange() {
    this.onChange({ $formatOptions: this.formatOptions });
  }

  decimalPlaces(precision: string) {
    if (!arguments.length) {
      return this.formatOptions[this.formatOptions.type].decimalPlaces;
    } else {
      this.formatOptions[this.formatOptions.type].decimalPlaces = Math.min(
        tryParseInt(precision),
        this.MAX_DECIMAL_PLACES
      );
    }
  }

  $onChanges(changed: angular.IOnChangesObject) {
    if (changed.formatOptions) {
      this.onColumnViewChanged();
    }
  }

  private isInColumnTypes(...types: string[]) {
    return this.formatOptions != null && types.indexOf(this.formatOptions.type) >= 0;
  }

  private onColumnViewChanged() {
    let { type } = this.formatOptions;
    // Reset type to automatic if the current one not found
    type = find(this.formatTypes, { id: type }) ? type : 'automatic';
    const mappedType = find(this.formatTypes, { id: type });

    // Use default settings if none currently set
    this.formatOptions[type] = this.formatOptions[type] ? this.formatOptions[type] : mappedType.defaultSettings();
    this.formatOptions.type = type;
  }
}

const FormatSettingsComponent: angular.IComponentOptions = {
  controller: FormatSettingsController,
  controllerAs: 'ctrl',
  template: template,
  bindings: {
    formatOptions: '<',
    enabledFormats: '<',
    onChange: '&',
  },
};

export { FormatSettingsController, FormatSettingsComponent };
